export type AnalyticsEvent =
  | "game_opened" | "tutorial_started" | "tutorial_completed"
  | "level_started" | "level_completed" | "level_failed"
  | "run_started" | "run_ended" | "treasure_collected" | "trap_triggered"
  | "combo_reached" | "reward_claimed" | "daily_challenge_started"
  | "daily_challenge_completed" | "daily_reward_claimed" | "settings_changed"
  | "curse_activated" | "secret_room_discovered"
  | "tile_revealed" | "treasure_found" | "curse_triggered"
  | "relic_fragment_collected" | "relic_unlocked"
  | "combo_milestone_reached" | "expedition_completed" | "expedition_failed"
  | "game_paused" | "game_resumed"
  | "achievement_unlocked" | "result_shared";

export const REQUIRED_GAMEPLAY_EVENTS = [
  "tile_revealed",
  "treasure_found",
  "curse_triggered",
  "secret_room_discovered",
  "relic_fragment_collected",
  "relic_unlocked",
  "combo_milestone_reached",
  "expedition_completed",
  "expedition_failed",
] as const satisfies readonly AnalyticsEvent[];

export type AnalyticsPayload = {
  playerId: string;
  sessionId: string;
  timestamp: string;
  levelId?: string;
  worldId?: string;
  runId?: string;
  [key: string]: string | number | boolean | undefined;
};

export interface Analytics {
  track(event: AnalyticsEvent, properties?: Record<string, string | number | boolean>): void;
}

export class CompositeAnalytics implements Analytics {
  constructor(private readonly sinks: readonly Analytics[]) {}
  track(event: AnalyticsEvent, properties: Record<string, string | number | boolean> = {}): void {
    for (const sink of this.sinks) sink.track(event, properties);
  }
}

export class ConsoleAnalytics implements Analytics {
  readonly sessionId = globalThis.crypto?.randomUUID?.() ?? `session-${Date.now()}`;
  constructor(private readonly playerId: string) {}
  track(event: AnalyticsEvent, properties: Record<string, string | number | boolean> = {}): void {
    const payload: AnalyticsPayload = {
      playerId: this.playerId, sessionId: this.sessionId,
      timestamp: new Date().toISOString(), ...properties,
    };
    if (import.meta.env.DEV) console.info(`[analytics] ${event}`, payload);
  }
}

/**
 * Keeps game telemetry local until the VibWebs release shell receives an
 * explicit analytics choice. The shell filters these events before GA4 sees
 * them, so player and session identifiers never leave the game.
 */
export class BrowserEventAnalytics implements Analytics {
  constructor(
    private readonly target: Pick<Window, "dispatchEvent"> | undefined =
      typeof window === "undefined" ? undefined : window,
  ) {}

  track(event: AnalyticsEvent, properties: Record<string, string | number | boolean> = {}): void {
    if (!this.target || typeof this.target.dispatchEvent !== "function" || typeof CustomEvent === "undefined") return;

    this.target.dispatchEvent(new CustomEvent("treasure-grid-analytics", {
      detail: {
        name: event,
        properties: { game_id: "treasure_grid", ...properties },
      },
    }));
  }
}
